/**
 * ตารางแนะนำไซซ์ — บอตใช้ตอบเวลาลูกค้าแจ้งสัดส่วน (เช่น "อก 38 เอว 32 สะโพก 50") → แนะนำไซซ์อัตโนมัติ
 * เชื่อมกับ: หน้า "ทดสอบบอต" (พิมพ์สัดส่วน → ดูไซซ์ที่ได้)
 *
 * ใช้ CrudManager กลาง — รองรับ measure ครบ (อก/เอว/สะโพก) แก้กฎสะโพกได้ด้วย
 */
import { CrudManager, type CrudField } from '@/components/CrudManager';
import { SizeRulesApi, type SizeRuleItem } from '@/lib/admin-api';

const GARMENTS = [
  { value: 'shirt', label: '👕 เสื้อ' },
  { value: 'pants', label: '🩳 กางเกง' },
];
const MEASURES = [
  { value: 'chest', label: 'รอบอก' },
  { value: 'waist', label: 'รอบเอว' },
  { value: 'hip', label: 'สะโพก' },
];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'].map((s) => ({ value: s, label: s }));
const measureLabel = (m: string) => MEASURES.find((x) => x.value === m)?.label ?? m;
const garmentLabel = (g: string) => GARMENTS.find((x) => x.value === g)?.label ?? g;

const FIELDS: CrudField[] = [
  { key: 'garment', label: 'ประเภท', type: 'select', required: true, options: GARMENTS, default: 'shirt', help: 'เสื้อ = วัดรอบอก · กางเกง = วัดเอว/สะโพก' },
  { key: 'measure', label: 'วัดจาก', type: 'select', required: true, options: MEASURES, default: 'chest', help: 'สัดส่วนที่ใช้ตัดสินไซซ์นี้' },
  { key: 'min_val', label: 'ตั้งแต่ (นิ้ว)', type: 'number', required: true, help: 'ค่าต่ำสุดของช่วง' },
  { key: 'max_val', label: 'ถึง (นิ้ว)', type: 'number', required: true, help: 'ค่าสูงสุดของช่วง' },
  { key: 'size', label: 'ไซซ์แนะนำ', type: 'select', required: true, options: SIZES, default: 'M', help: 'ไซซ์ที่บอตจะแนะนำเมื่อสัดส่วนอยู่ในช่วงนี้' },
  { key: 'active', label: 'เปิดใช้งาน', type: 'toggle', default: true },
  { key: 'notes', label: 'หมายเหตุ', type: 'text', help: 'บันทึกภายใน (ไม่ส่งให้ลูกค้า)' },
];

export default function SizeRulesPage() {
  return (
    <CrudManager<SizeRuleItem>
      title="📏 ตารางแนะนำไซซ์"
      addLabel="เพิ่มช่วงไซซ์"
      help={
        <span>
          บอตใช้ตอบเวลาลูกค้าแจ้งสัดส่วน เช่น “อก 38 เอว 32 สะโพก 50” → แนะนำไซซ์อัตโนมัติ
          (เสื้อดูรอบอก · กางเกงดูเอว+สะโพก แล้วเลือกไซซ์ที่ใหญ่กว่า). ค่าตั้งต้นมาจากที่แอดมินแนะนำลูกค้าจริง
        </span>
      }
      queryKey="size-rules"
      api={SizeRulesApi}
      fields={FIELDS}
      brandScoped
      hardDelete
      groupBy={(r) => `${garmentLabel(r.garment)} · ${measureLabel(r.measure)}`}
      sortRows={(a, b) => a.garment.localeCompare(b.garment) || a.measure.localeCompare(b.measure) || a.min_val - b.min_val}
      rowTitle={(r) => <>ไซซ์ {r.size}</>}
      rowSubtitle={(r) => <>{r.min_val}–{r.max_val} นิ้ว{r.notes ? ` · ${r.notes}` : ''}</>}
    />
  );
}
